import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { moduleService, ModuleFilters, CreateModuleData, UpdateModuleData } from '../services';

export const useModules = (filters?: ModuleFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['modules', filters],
    queryFn: async () => {
      const response = await moduleService.getModules(filters);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateModuleData) => moduleService.createModule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateModuleData }) =>
      moduleService.updateModule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => moduleService.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => moduleService.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
  });

  return {
    modules: data,
    isLoading,
    error,
    createModule: createMutation.mutateAsync,
    updateModule: updateMutation.mutateAsync,
    deleteModule: deleteMutation.mutateAsync,
    toggleStatus: toggleStatusMutation.mutateAsync,
  };
};

export const useModule = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['modules', id],
    queryFn: async () => {
      const response = await moduleService.getModule(id);
      return response.data;
    },
    enabled: !!id,
  });

  return {
    module: data,
    isLoading,
    error,
  };
};
